import React, { ReactNode } from 'react';
import { Link, Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  AppHeaderBar,
  SidebarUserFooter,
  UserMenu,
  CompanySwitcher,
  Button,
  MirorUIProvider,
  MirorPageMetaProvider,
  useSetPageMeta,
  PageSkeleton,
  type NavLeaf,
  type LinkComponentProps,
} from '@miror/ui-web';
import { MirorLabsMark } from '@miror/branding-sdk/react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { themeStore } from '../../contexts/ThemeContext';
import { useSidebar } from '../../stores/sidebar';
import { APP_VERSION } from '../../lib/version';
import { NAV_GROUPS } from './nav';
import { AppHub } from './AppHub';
import type { User } from '../../types/index';

/**
 * Gate de permissão do Task (espelha o padrão do Miror Pro / web da suíte):
 * super_admin sempre passa; caso contrário exige a chave plana (task:<area>:<view>)
 * presente em `user.permissions` (resolvidas na empresa ativa pelo /auth/me).
 */
export function hasPermission(user: User | null | undefined, perm: string): boolean {
  return !!user?.is_super_admin || !!user?.permissions?.includes(perm);
}

// URL do onboarding do Social Miror — "Nova empresa" abre lá em nova aba (padrão Pro).
const NOVA_EMPRESA_HREF = 'https://social.mirorlabs.com/onboarding';

// Adapter para o AppShell/composites usarem o <Link> do react-router.
const LinkAdapter = ({ to, children, className, target, rel }: LinkComponentProps) => (
  <Link to={to} className={className} target={target} rel={rel}>
    {children}
  </Link>
);

// Logo do app — marca corporativa MirorLabs (padrão da suíte Pro/Social,
// currentColor: herda a cor do tema, sem PNG fixo).
const TaskLogo = ({ className }: { className?: string }) => <MirorLabsMark className={className} />;

/**
 * Título default por rota. Páginas que precisam de título dinâmico (detalhe de
 * tarefa/projeto) sobrescrevem via `useSetPageMeta` — como o efeito da página
 * roda depois do `RouteMetaFallback` (descendente roda por último), o valor da
 * página vence. Mapa simples por prefixo, na ordem mais-específico → genérico.
 */
const ROUTE_TITLES: Array<[string, string]> = [
  ['/tasks/recorrencias', 'Recorrências'],
  ['/tasks/', 'Tarefa'],
  ['/tasks', 'Tarefas'],
  ['/projects/', 'Projeto'],
  ['/projects', 'Projetos'],
  ['/clientes', 'Clientes'],
  ['/horas/dashboard', 'Dashboard de Horas'],
  ['/horas/admin', 'Administração de Horas'],
  ['/horas', 'Controle de Horas'],
  ['/empresa', 'Empresa'],
  ['/integracoes', 'Integrações'],
  ['/plano-uso', 'Plano e uso'],
  ['/users', 'Equipe & acessos'],
  ['/calendar', 'Calendário'],
  ['/settings', 'Minha conta'],
  ['/painel-miror', 'Painel Miror'],
  ['/dashboard', 'Dashboard'],
  ['/', 'Dashboard'],
];

function titleForPath(pathname: string): string {
  // Mais-específico primeiro: prefixos com '/' final batem em rotas de detalhe.
  const match = ROUTE_TITLES.find(([prefix]) =>
    prefix.endsWith('/') ? pathname.startsWith(prefix) : pathname === prefix,
  );
  if (match) return match[1];
  // Fallback amplo por prefixo (ex.: /horas/qualquer).
  const broad = ROUTE_TITLES.find(([prefix]) => prefix !== '/' && pathname.startsWith(prefix));
  return broad ? broad[1] : 'Glivo Task';
}

const RouteMetaFallback: React.FC = () => {
  const { pathname } = useLocation();
  useSetPageMeta({ title: titleForPath(pathname) });
  return null;
};

/**
 * Layout root das rotas protegidas. Renderiza o `<AppShell>` do @miror/ui-web
 * uma única vez e troca apenas o `<Outlet/>` na navegação (shell persistente,
 * sem re-montar a sidebar a cada rota). O título do header sticky vem do
 * `<MirorPageMetaProvider>`: páginas usam `useSetPageMeta`/`<PageHeader>` e o
 * `RouteMetaFallback` garante um título sensato por rota mesmo sem código.
 */
const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, loading, logout, companies, companyId, setActiveCompany } = useAuth();
  const collapsed = useSidebar((s) => s.collapsed);
  const toggle = useSidebar((s) => s.toggle);
  const expandedSections = useSidebar((s) => s.expandedSections);
  const toggleSection = useSidebar((s) => s.toggleSection);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filtra itens por permissão (espelha o MirorProShell): super_admin bypassa;
  // item com `meta.permission` some quando o user não tem a chave; item sem
  // gate (Minha conta) aparece pra todos; `superAdminOnly` continua no super.
  // Grupos que ficam vazios após o filtro somem inteiros.
  const navGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => {
      if (i.meta?.superAdminOnly && !user?.is_super_admin) return false;
      if (user?.is_super_admin) return true;
      const perm = i.meta?.permission as string | undefined;
      if (perm && !hasPermission(user, perm)) return false;
      return true;
    }),
  })).filter((g) => g.items.length > 0);

  const isItemActive = (item: NavLeaf): boolean => {
    const base = item.activePath ?? item.to.split('?')[0]!;
    if (item.meta?.prefix) {
      return location.pathname === base || location.pathname.startsWith(`${base}/`);
    }
    return location.pathname === base;
  };

  if (loading) {
    return <PageSkeleton variant="dashboard" />;
  }
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <MirorUIProvider
      linkComponent={LinkAdapter}
      brand={{ appName: 'Glivo Task', appSubtitle: `Glivo Labs · v${APP_VERSION}`, Logo: TaskLogo }}
    >
      <MirorPageMetaProvider>
        <RouteMetaFallback />
        <AppShell
          brand={{ to: '/dashboard' }}
          navGroups={navGroups}
          isItemActive={isItemActive}
          collapsed={collapsed}
          onToggleCollapsed={toggle}
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
          sidebarFooter={(ctx) => (
            <SidebarUserFooter
              userEmail={user?.email}
              onLogout={handleLogout}
              themeStore={themeStore}
              {...ctx}
            />
          )}
          header={
            <AppHeaderBar
              actions={
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <AppHub />
                  <CompanySwitcher
                    className="glivo-company-switcher"
                    companies={companies.map((c) => ({ id: c.id, name: c.nome }))}
                    activeId={companyId}
                    onChange={(id) => {
                      if (id) void setActiveCompany(id);
                    }}
                  />
                  {user?.is_super_admin && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={NOVA_EMPRESA_HREF} target="_blank" rel="noopener noreferrer">
                        <Plus className="mr-1 h-4 w-4" />
                        Nova empresa
                      </a>
                    </Button>
                  )}
                  <UserMenu
                    user={user ? { name: user.nome, email: user.email } : undefined}
                    onLogout={handleLogout}
                    onOpenSettings={() => navigate('/settings')}
                  />
                </div>
              }
            />
          }
        >
          {/* Container de conteúdo alinhado ao Miror Pro: padding p-4 sm:p-6 +
              transição mkt-fade-in entre páginas (key={pathname} re-monta). */}
          <div key={location.pathname} className="mkt-fade-in p-4 sm:p-6">
            <Outlet />
          </div>
        </AppShell>
      </MirorPageMetaProvider>
    </MirorUIProvider>
  );
};

/**
 * Wrapper de rota gateado por permissão (task:<area>:<view>). super_admin sempre
 * passa. Só redireciona quando a sessão já carregou e o user existe mas não tem
 * a permissão — enquanto `loading` (ou user ainda não hidratado) NÃO redireciona,
 * pra não expulsar o user num flash antes das permissões chegarem do /auth/me.
 */
export const RequirePermission: React.FC<{ permission: string; children: ReactNode }> = ({
  permission,
  children,
}) => {
  const { user, loading } = useAuth();
  if (!loading && user && !hasPermission(user, permission)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

export default AppLayout;
